import { useState, useEffect } from 'react';
import * as ivyChatApi from '@/services/ivyChatApi';
import { type ClaudeProvider, type ProviderStatus } from '@/services/ivyChatApi';
import { type QualityTier, QUALITY_TIER_OPTIONS } from '@/types/models';
import { COMING_SOON_LABEL } from '@agentic-ivy/shared-types';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onClose: () => void;
  qualityTier?: QualityTier;
  onQualityTierChange?: (tier: QualityTier) => void;
}

const PROVIDER_TABS: { id: ClaudeProvider; label: string; description: string }[] = [
  { id: 'claude_token', label: 'Claude Token', description: 'OAuth token from claude setup-token' },
  { id: 'anthropic', label: 'Anthropic API', description: 'Direct API key from console.anthropic.com' },
  { id: 'bedrock', label: 'AWS Bedrock', description: 'Use Claude via Amazon Bedrock' },
];

export function ClaudeTokenModal({ open, onClose, qualityTier, onQualityTierChange }: Props) {
  const [activeTab, setActiveTab] = useState<ClaudeProvider>('claude_token');
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Claude Token state
  const [claudeToken, setClaudeToken] = useState('');
  // Anthropic API Key state
  const [anthropicKey, setAnthropicKey] = useState('');
  // Bedrock state
  const [bedrockToken, setBedrockToken] = useState('');
  const [bedrockRegion, setBedrockRegion] = useState('us-east-1');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    ivyChatApi.getProviderStatus().then(status => {
      setProviderStatus(status);
      setActiveTab(status.activeProvider);
      setLoading(false);
    });
  }, [open]);

  if (!open) return null;

  const handleSaveClaude = async () => {
    const clean = claudeToken.replace(/[^\x20-\x7E]/g, '').trim();
    if (!clean) return;
    setSaving(true);
    setError(null);
    const result = await ivyChatApi.saveClaudeToken(clean);
    if (result.success) {
      await ivyChatApi.setActiveProvider('claude_token');
      setSaved(true);
      setClaudeToken('');
      setTimeout(onClose, 600);
    } else {
      setError(result.error || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSaveAnthropic = async () => {
    const clean = anthropicKey.replace(/[^\x20-\x7E]/g, '').trim();
    if (!clean) return;
    setSaving(true);
    setError(null);
    const result = await ivyChatApi.saveApiKey('ANTHROPIC', clean);
    if ('error' in result) {
      setError(result.error);
    } else {
      await ivyChatApi.setActiveProvider('anthropic');
      setSaved(true);
      setAnthropicKey('');
      setTimeout(onClose, 600);
    }
    setSaving(false);
  };

  const handleSaveBedrock = async () => {
    const clean = bedrockToken.replace(/[^\x20-\x7E]/g, '').trim();
    if (!clean) return;
    setSaving(true);
    setError(null);
    const payload = JSON.stringify({ bearerToken: clean, region: bedrockRegion });
    const result = await ivyChatApi.saveApiKey('BEDROCK', payload);
    if ('error' in result) {
      setError(result.error);
    } else {
      await ivyChatApi.setActiveProvider('bedrock');
      setSaved(true);
      setBedrockToken('');
      setTimeout(onClose, 600);
    }
    setSaving(false);
  };

  const handleActivate = async (provider: ClaudeProvider) => {
    setSaving(true);
    await ivyChatApi.setActiveProvider(provider);
    setProviderStatus(prev => prev ? { ...prev, activeProvider: provider } : prev);
    setSaving(false);
  };

  const handleDeleteClaude = async () => {
    setSaving(true);
    await ivyChatApi.deleteClaudeToken();
    setProviderStatus(prev => prev ? {
      ...prev,
      providers: { ...prev.providers, claude_token: { hasKey: false, hint: null } },
    } : prev);
    setSaving(false);
  };

  const handleDeleteByok = async (provider: 'ANTHROPIC' | 'BEDROCK') => {
    setSaving(true);
    const keys = await ivyChatApi.listApiKeys();
    const key = keys.find(k => k.provider === provider);
    if (key) await ivyChatApi.deleteApiKey(key.id);
    const providerKey = provider === 'ANTHROPIC' ? 'anthropic' : 'bedrock';
    setProviderStatus(prev => prev ? {
      ...prev,
      providers: { ...prev.providers, [providerKey]: { hasKey: false, hint: null } },
    } : prev);
    setSaving(false);
  };

  const claudeTokenType = claudeToken.startsWith('sk-ant-oat')
    ? 'OAuth Token (subscription)'
    : claudeToken.startsWith('sk-ant-api')
      ? 'API Key (pay-per-use)'
      : claudeToken
        ? 'Unknown format'
        : '';

  const p = providerStatus?.providers;
  const isActive = providerStatus?.activeProvider === activeTab;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="block w-[460px] max-w-[calc(100vw-2rem)] rounded-xl p-5">
        <DialogTitle className="text-base font-semibold text-foreground mb-1">Your model key</DialogTitle>
        <DialogDescription className="text-xs text-muted-foreground mb-3">
          Choose which kind of key to use. It is encrypted and stored securely.
        </DialogDescription>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand border-t-transparent" />
          </div>
        ) : (
          <>
            {/* Provider tabs */}
            <div className="flex items-center bg-black/5 dark:bg-white/5 rounded-full p-0.5 mb-4">
              {PROVIDER_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setError(null); setSaved(false); }}
                  className={`flex-1 px-2 py-1.5 text-[11px] font-medium rounded-full transition-all duration-200 relative ${
                    activeTab === tab.id
                      ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.label}
                  {providerStatus?.activeProvider === tab.id && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Claude Token tab */}
            {activeTab === 'claude_token' && (
              <div>
                {p?.claude_token.hasKey && (
                  <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-muted/30 border border-border">
                    <div>
                      <span className="text-[10px] text-muted-foreground block">Current token</span>
                      <span className="text-xs font-mono text-foreground">{p.claude_token.hint}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isActive && (
                        <button onClick={() => handleActivate('claude_token')} disabled={saving}
                          className="text-[10px] text-brand hover:text-brand/80 transition-colors font-medium">
                          Activate
                        </button>
                      )}
                      <button onClick={handleDeleteClaude} disabled={saving}
                        className="text-[10px] text-destructive hover:text-destructive/80 transition-colors">
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground block mb-1">
                    {p?.claude_token.hasKey ? 'Replace with new token' : 'Enter your Claude token'}
                  </label>
                  <input
                    type="password"
                    value={claudeToken}
                    onChange={e => { setClaudeToken(e.target.value); setSaved(false); setError(null); }}
                    placeholder="sk-ant-oat01-... or sk-ant-api..."
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-ring/50 focus:ring-1 focus:ring-ring/20"
                  />
                  {claudeTokenType && (
                    <span className="text-[10px] text-muted-foreground/60 mt-1 block">{claudeTokenType}</span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground mb-3">
                  Get a token: run <code className="text-info font-mono">claude setup-token</code> or visit <code className="text-info font-mono">console.anthropic.com</code>
                </div>
                {error && <span className="text-[10px] text-destructive mb-2 block">{error}</span>}
                <div className="flex justify-end gap-2">
                  <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                  <button onClick={handleSaveClaude} disabled={!claudeToken.trim() || saving}
                    className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-colors disabled:opacity-40 ${saved ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save & Activate'}
                  </button>
                </div>
              </div>
            )}

            {/* Anthropic API Key tab */}
            {activeTab === 'anthropic' && (
              <div>
                {p?.anthropic.hasKey && (
                  <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-muted/30 border border-border">
                    <div>
                      <span className="text-[10px] text-muted-foreground block">Current key</span>
                      <span className="text-xs font-mono text-foreground">{p.anthropic.hint}...</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isActive && (
                        <button onClick={() => handleActivate('anthropic')} disabled={saving}
                          className="text-[10px] text-brand hover:text-brand/80 transition-colors font-medium">
                          Activate
                        </button>
                      )}
                      <button onClick={() => handleDeleteByok('ANTHROPIC')} disabled={saving}
                        className="text-[10px] text-destructive hover:text-destructive/80 transition-colors">
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground block mb-1">
                    {p?.anthropic.hasKey ? 'Replace with new key' : 'Enter your Anthropic API key'}
                  </label>
                  <input
                    type="password"
                    value={anthropicKey}
                    onChange={e => { setAnthropicKey(e.target.value); setSaved(false); setError(null); }}
                    placeholder="sk-ant-api03-..."
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-ring/50 focus:ring-1 focus:ring-ring/20"
                  />
                </div>
                <div className="text-[10px] text-muted-foreground mb-3">
                  Create an API key at <code className="text-info font-mono">console.anthropic.com/settings/keys</code>. Pay-per-use billing.
                </div>
                {error && <span className="text-[10px] text-destructive mb-2 block">{error}</span>}
                <div className="flex justify-end gap-2">
                  <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                  <button onClick={handleSaveAnthropic} disabled={!anthropicKey.trim() || saving}
                    className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-colors disabled:opacity-40 ${saved ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save & Activate'}
                  </button>
                </div>
              </div>
            )}

            {/* AWS Bedrock tab */}
            {activeTab === 'bedrock' && (
              <div>
                {p?.bedrock.hasKey && (
                  <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-muted/30 border border-border">
                    <div>
                      <span className="text-[10px] text-muted-foreground block">Bedrock configured</span>
                      <span className="text-xs font-mono text-foreground">{p.bedrock.hint}...</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isActive && (
                        <button onClick={() => handleActivate('bedrock')} disabled={saving}
                          className="text-[10px] text-brand hover:text-brand/80 transition-colors font-medium">
                          Activate
                        </button>
                      )}
                      <button onClick={() => handleDeleteByok('BEDROCK')} disabled={saving}
                        className="text-[10px] text-destructive hover:text-destructive/80 transition-colors">
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground block mb-1">
                    {p?.bedrock.hasKey ? 'Replace bearer token' : 'AWS Bedrock Bearer Token'}
                  </label>
                  <input
                    type="password"
                    value={bedrockToken}
                    onChange={e => { setBedrockToken(e.target.value); setSaved(false); setError(null); }}
                    placeholder="Bearer token from AWS SSO / session"
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-ring/50 focus:ring-1 focus:ring-ring/20"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground block mb-1">AWS Region</label>
                  <select
                    value={bedrockRegion}
                    onChange={e => setBedrockRegion(e.target.value)}
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-ring/50 focus:ring-1 focus:ring-ring/20"
                  >
                    <option value="us-east-1">us-east-1 (N. Virginia)</option>
                    <option value="us-west-2">us-west-2 (Oregon)</option>
                    <option value="eu-west-1">eu-west-1 (Ireland)</option>
                    <option value="ap-northeast-1">ap-northeast-1 (Tokyo)</option>
                  </select>
                </div>
                <div className="text-[10px] text-muted-foreground mb-3">
                  Uses <code className="text-info font-mono">AWS_BEARER_TOKEN_BEDROCK</code>. Get a token via <code className="text-info font-mono">aws sso login</code> or IAM Identity Center.
                </div>
                {error && <span className="text-[10px] text-destructive mb-2 block">{error}</span>}
                <div className="flex justify-end gap-2">
                  <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                  <button onClick={handleSaveBedrock} disabled={!bedrockToken.trim() || saving}
                    className={`px-4 py-1.5 text-xs rounded-lg font-medium transition-colors disabled:opacity-40 ${saved ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save & Activate'}
                  </button>
                </div>
              </div>
            )}

            {/* Quality Tier (always shown at bottom) */}
            {qualityTier && onQualityTierChange && (
              <div className="mt-3 pt-3 border-t border-border">
                <label className="text-xs text-muted-foreground block mb-2">Quality Tier</label>
                <div className="flex items-center bg-black/5 dark:bg-white/5 rounded-full p-0.5">
                  {QUALITY_TIER_OPTIONS.map((tier) => (
                    <button
                      key={tier.id}
                      type="button"
                      disabled={tier.comingSoon}
                      onClick={() => onQualityTierChange(tier.id)}
                      className={`flex-1 px-3 py-1.5 text-[11px] font-medium rounded-full transition-all duration-200 ${
                        tier.comingSoon
                          ? 'cursor-not-allowed text-muted-foreground opacity-60'
                          : qualityTier === tier.id
                            ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                      }`}
                      title={tier.description}
                    >
                      {tier.label}
                      {tier.comingSoon && <span className="ml-1 font-normal">{COMING_SOON_LABEL}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
